import {useState, ChangeEvent, useCallback} from 'react';
import {TransitionModal} from './transition-modal';
import {TEXT_INPUT_STYLE, BUTTON_STYLE_MAIN_BLOCK, BUTTON_STYLE_CANCEL_FLEX, BUTTON_STYLE_MAIN_FLEX} from './styles';
import {PakConfigInputs} from './pak-config-inputs';
import {ChannelConfiguration, PakConfiguration, PineridgeChannel} from './pineridge-channel';
import {useEventHandler, emitEvent, Event} from './events';
import {isPakConfiguration, isMobileBrowser} from './util';

interface ChannelConfigModalProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  configSource: ChannelConfiguration | PineridgeChannel;
  setConfig: (config: ChannelConfiguration) => void;
}

export function ChannelConfigModal({isOpen, setOpen, configSource, setConfig}: ChannelConfigModalProps) {
  const isEdit = configSource instanceof PineridgeChannel; // Are we editing an existing channel or creating a new channel?
  const title = isEdit ? configSource.loggingId : 'Create Channel';
  const initialConfig = isEdit ? configSource.config : configSource;
  const isPak = isPakConfiguration(initialConfig); // Is the source configured with a PAK or a SAT?

  const [channelConfig, setChannelConfig] = useState<ChannelConfiguration>(initialConfig);
  const [metadata, setMetadata] = useState(channelConfig.metadata);
  const [hasInputError, setHasInputError] = useState(false);

  useEventHandler(
    Event.PINERIDGE_CHANNEL_SAT_UPDATED,
    (c: PineridgeChannel) => {
      if (!isEdit || c.pineridgeChannelId !== configSource.pineridgeChannelId) {
        return;
      }

      setOpen(false);
    },
    [isEdit, configSource]
  );

  const cancel = useCallback(() => {
    setChannelConfig(initialConfig);
    setMetadata(initialConfig.metadata);
    setOpen(false);
  }, [initialConfig]);

  function submit(): void {
    setConfig({
      ...channelConfig,
      metadata,
    });
    setOpen(false);
  }

  const handleUpdatePakConfig = useCallback(
    (config: PakConfiguration) => {
      setChannelConfig({...config, metadata});
    },
    [metadata]
  );

  const handlePakConfigError = useCallback((hasError: boolean) => {
    setHasInputError(hasError);
  }, []);

  function handleChangeMetadata(e: ChangeEvent<HTMLTextAreaElement>): void {
    const value = e.target.value;
    setMetadata(value);
  }

  const handlePasteSAT = useCallback(() => {
    emitEvent(Event.USER_MOBILE_PASTE, null);
  }, []);

  function UpdateSat() {
    return (
      <div className="justify-center text-center m-10">
        {isMobileBrowser() ? (
          <button className={`${BUTTON_STYLE_MAIN_BLOCK} h-16`} onClick={handlePasteSAT}>
            <p className="text-xl font-bold">Click to Paste SAT</p>
          </button>
        ) : (
          <p className="text-xl font-extrabold">PASTE ANYWHERE TO UPDATE SAT</p>
        )}
      </div>
    );
  }

  return (
    <TransitionModal isOpen={isOpen} onClose={cancel} title={title}>
      {isPak ? (
        <PakConfigInputs
          isOpen={isOpen}
          initialConfig={initialConfig}
          updateConfig={handleUpdatePakConfig}
          updateConfigError={handlePakConfigError}
          disabled={isEdit}
        />
      ) : (
        <UpdateSat />
      )}
      <div className="mt-2 text-sm grid grid-cols-3">
        <div className="mr-1 mb-1 font-bold">Metadata</div>
        <div>{/* This is just here to fill out the grid row */}</div>
        <div>{/* This is just here to fill out the grid row */}</div>

        <div className="col-span-3">
          <textarea className={TEXT_INPUT_STYLE} value={metadata} onChange={handleChangeMetadata} />
        </div>
      </div>
      <div className="px-4 py-2 flex flex-row-reverse sm:px-6">
        <button type="button" className={BUTTON_STYLE_MAIN_FLEX + ' m-1'} onClick={submit} disabled={hasInputError}>
          Submit
        </button>
        <button type="button" className={BUTTON_STYLE_CANCEL_FLEX + ' m-1'} onClick={cancel}>
          Cancel
        </button>
      </div>
    </TransitionModal>
  );
}
